import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import PropTypes from "prop-types";
import {
  FilledInput,
  Button,
  FormHelperText,
} from "@mui/material";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";

import {
  acceptedImagesFilesTypes,
  acceptedImagesFilesMaxSize,
  acceptedPdfFilesType,
  acceptedPdfFilesMaxSize,
} from "../../utils/Constants";
import { PdfIcon } from "../../utils/Icons";
import messages from "../../assets/locale/messages";
import UIUploadedFile from "../ui/UIUploadedFile";
import UIUploadProgress from "../ui/UIUploadProgress";
import UIUploadSelectedFile from "../ui/UIUploadSelectedFile/UIUploadSelectedFile";
import "./Upload.scss";

const IMAGE_EXT_PATTERN = /\.(jpe?g|png|gif|bmp|svg|webp|jfif|heic|avif)$/i;

const isPdfUploadValue = (value, fileName = "") => {
  if (typeof value === "string") {
    if (value.startsWith("data:image")) return false;
    return value.startsWith("data:application/pdf");
  }
  if (value && typeof value === "object" && typeof value.type === "string") {
    if (value.type.startsWith("image/")) return false;
    return value.type === "application/pdf";
  }
  return /\.pdf$/i.test(fileName);
};

const isImageUploadValue = (value, fileName = "") => {
  if (typeof value === "string") {
    return (
      value.startsWith("data:image") ||
      value.startsWith("blob:") ||
      /^https?:\/\//.test(value)
    );
  }
  if (value && typeof value === "object" && typeof value.type === "string") {
    return value.type.startsWith("image/");
  }
  return IMAGE_EXT_PATTERN.test(fileName);
};

const Upload = ({
  accept,
  onChange,
  label,
  required,
  disabled,
  img,
  name,
  multiple,
  className,
  onChangeError,
  isFullWidthImage,
  fullWidthImageClassName,
  id,
  isInputHasErr,
  fieldLabel,
  pdf,
  isImage,
  isPdf,
  isBluredBackground,
  wrapperClasses,
  errorMsg,
  uploadArea,
  onRemoveAt,
  isUploading,
  uploadProgress,
  uploadProgressLabel,
  uploadCompleted,
  showUploadProgress,
  maxFileSizeBytes,
  invalidTypeMessage,
  invalidSizeMessage,
  onPickerDismissedWithoutFile,
}) => {
  const lang = useSelector((state) => state.locale.lang);
  const { shared, inputsValidations } = messages[lang];
  const [error, setError] = useState(null);
  const [pdfFileName, setpdfFileName] = useState("");
  const [selectedFileName, setSelectedFileName] = useState("");

  const filePickerOpenRef = useRef(false);
  const hasSelectedFileRef = useRef(false);

  const inputId = id || "raised-button-file";
  const normalizedFieldLabel =
    typeof fieldLabel === "string" ? fieldLabel.trim() : fieldLabel;

  useEffect(() => {
    const el = document.getElementById(inputId);
    if (!el) return;
    el.accept = accept;
    el.multiple = !!multiple;
  }, [accept, id, multiple, inputId]);

  const resolvedMaxImageSize = maxFileSizeBytes ?? acceptedImagesFilesMaxSize;
  const resolvedMaxPdfSize = maxFileSizeBytes ?? acceptedPdfFilesMaxSize;

  const reportTypeError = (fallbackKey) => {
    const message = invalidTypeMessage ?? fallbackKey;
    setError(message);
    onChangeError && onChangeError(message);
  };

  const reportSizeError = (fallbackKey) => {
    const message = invalidSizeMessage ?? fallbackKey;
    setError(message);
    onChangeError && onChangeError(message);
  };

  const acceptAllowsFile = (file) => {
    if (!accept) return true;
    const rules = accept
      .split(",")
      .map((value) => value.trim())
      .filter(Boolean);
    const extRules = rules.filter((rule) => rule.startsWith("."));
    const mimeRules = rules.filter((rule) => !rule.startsWith("."));
    const name = String(file?.name || "").toLowerCase();

    if (
      extRules.length &&
      !extRules.some((rule) => name.endsWith(rule.toLowerCase()))
    ) {
      return false;
    }

    if (!mimeRules.length) return true;

    return mimeRules.some((rule) => {
      if (rule.endsWith("/*")) {
        return Boolean(file?.type?.startsWith(rule.replace("*", "")));
      }
      return file?.type === rule;
    });
  };

  const validateImagesFiles = (imagesFilesList) => {
    return Array.from(imagesFilesList).every((file) => {
      if (!acceptAllowsFile(file)) {
        reportTypeError("uploadTypeError");
        return false;
      }
      if (!acceptedImagesFilesTypes.includes(file.type)) {
        reportTypeError("uploadTypeError");
        return false;
      }
      if (file.size > resolvedMaxImageSize) {
        reportSizeError("uploadSizeError");
        return false;
      }
      setError(null);
      onChangeError && onChangeError(null);
      return true;
    });
  };

  const validatePdfFiles = (pdfFilesList) => {
    return Array.from(pdfFilesList).every((file) => {
      if (!acceptedPdfFilesType.includes(file.type)) {
        reportTypeError("pdfUploadTypeError");
        return false;
      }
      if (file.size > resolvedMaxPdfSize) {
        reportSizeError("pdfUploadSizeError");
        return false;
      }
      setError(null);
      onChangeError && onChangeError(null);
      return true;
    });
  };

  const handleFilesSelected = (filesList) => {
    filePickerOpenRef.current = false;
    if (!filesList?.length || !filesList[0]) return;
    setSelectedFileName(filesList[0]?.name ?? "");

    /** PDF or image (single field) — PDF, JPG, PNG only (registration docs). */
    if (isImage && isPdf) {
      const file = filesList[0];
      const allowedPdfOrJpegPng = [
        "application/pdf",
        "image/jpeg",
        "image/png",
        "image/jpg",
      ];
      if (!allowedPdfOrJpegPng.includes(file.type)) {
        reportTypeError("uploadTypeError");
        return;
      }
      const isPdfFile = acceptedPdfFilesType.includes(file.type);
      if (isPdfFile) {
        if (!validatePdfFiles(filesList)) return;
        setpdfFileName(file?.name ?? "");
      } else {
        if (!validateImagesFiles(filesList)) return;
        setpdfFileName("");
      }
      if (multiple) {
        const filesAsArray = [...filesList];
        const filesSrc = filesAsArray.map((f) => URL.createObjectURL(f));
        onChange(filesSrc, filesAsArray);
        return;
      }
      const reader = new FileReader();
      reader.onload = (event) => {
        onChange(event.target.result, file);
      };
      reader.readAsDataURL(file);
      return;
    }

    if (isImage) {
      if (!validateImagesFiles(filesList)) return;
      if (multiple) {
        const filesAsArray = [...filesList];
        const filesSrc = filesAsArray.map((file) => URL.createObjectURL(file));
        onChange(filesSrc, filesAsArray);
      } else {
        const file = filesList[0];
        const reader = new FileReader();
        reader.onload = (event) => {
          onChange(event.target.result, file);
        };
        reader.readAsDataURL(file);
      }
      return;
    }

    if (isPdf) {
      if (!validatePdfFiles(filesList)) return;
      if (multiple) {
        const filesAsArray = [...filesList];
        const filesSrc = filesAsArray.map((file) => URL.createObjectURL(file));
        onChange(filesSrc, filesAsArray);
      } else {
        const file = filesList[0];
        const pdfReader = new FileReader();
        pdfReader.onload = (event) => {
          onChange(event.target.result, file);
        };
        pdfReader.readAsDataURL(file);
        setpdfFileName(file?.name ?? "");
      }
    }
  };

  const preventDragDefaults = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const previewList = Array.isArray(img) ? img : img ? [img] : [];
  const showMultiImageCustomArea = multiple && isImage && uploadArea;

  const pdfOrImageSingle =
    isImage && isPdf
      ? typeof img === "string"
        ? img
        : typeof pdf === "string"
          ? pdf
          : null
      : null;
  const showPdfOrImageAsImage =
    pdfOrImageSingle &&
    typeof pdfOrImageSingle === "string" &&
    pdfOrImageSingle.startsWith("data:image");
  const showPdfOrImageAsPdf =
    pdfOrImageSingle &&
    typeof pdfOrImageSingle === "string" &&
    pdfOrImageSingle.startsWith("data:application/pdf");

  const hasSelectedFile = Boolean(
    pdf ||
      selectedFileName ||
      pdfFileName ||
      (pdfOrImageSingle && typeof pdfOrImageSingle === "string"),
  );

  hasSelectedFileRef.current = hasSelectedFile;

  useEffect(() => {
    if (!onPickerDismissedWithoutFile) return undefined;

    const handleWindowFocus = () => {
      if (!filePickerOpenRef.current) return;

      window.setTimeout(() => {
        if (!filePickerOpenRef.current) return;
        filePickerOpenRef.current = false;

        if (!hasSelectedFileRef.current && !isUploading) {
          onPickerDismissedWithoutFile();
        }
      }, 400);
    };

    window.addEventListener("focus", handleWindowFocus);
    return () => window.removeEventListener("focus", handleWindowFocus);
  }, [isUploading, onPickerDismissedWithoutFile]);

  const showUploadSuccess = Boolean(uploadCompleted && hasSelectedFile);

  const showSelectedFilePreview =
    !showUploadSuccess &&
    !showMultiImageCustomArea &&
    (hasSelectedFile || isUploading);

  const fieldHasError = Boolean(isInputHasErr || error);

  const resolvedHelperError = (() => {
    // The pick-time error (size/format) is more specific than the form's "required".
    if (error) {
      return shared[error] ?? inputsValidations[error] ?? error;
    }
    if (isInputHasErr && errorMsg) {
      return inputsValidations[errorMsg] ?? errorMsg;
    }
    return null;
  })();

  const clearSelectedFile = () => {
    setSelectedFileName("");
    setpdfFileName("");
    setError(null);
    onChangeError && onChangeError(null);
    onChange(null);
  };

  const displayFileName = selectedFileName || pdfFileName || normalizedFieldLabel;
  const selectedIsPdf =
    showPdfOrImageAsPdf ||
    isPdfUploadValue(pdf, displayFileName) ||
    isPdfUploadValue(img, displayFileName);
  const selectedIsImage =
    isImage &&
    !selectedIsPdf &&
    (showPdfOrImageAsImage ||
      isImageUploadValue(img, displayFileName) ||
      isImageUploadValue(pdf, displayFileName));

  const selectedFilePreview = (
    <UIUploadSelectedFile
      fileName={displayFileName}
      preview={
        typeof pdfOrImageSingle === "string"
          ? pdfOrImageSingle
          : typeof img === "string"
            ? img
            : ""
      }
      isPdf={selectedIsPdf}
      isImage={selectedIsImage}
      hasError={fieldHasError}
      removeAriaLabel={shared.removeFile}
      onRemove={disabled ? undefined : clearSelectedFile}
    />
  );

  return (
    <div className={`position-relative ${className}`}>
      {normalizedFieldLabel ? (
        <div className="field-label">
          {normalizedFieldLabel}
          {required ? <span className="err-asterisk">*</span> : null}
        </div>
      ) : null}
      <FilledInput
        style={{ display: "none" }}
        id={inputId}
        type="file"
        accept={accept}
        onClick={() => {
          if (disabled) return;
          filePickerOpenRef.current = true;
        }}
        onChange={(e) => {
          handleFilesSelected(e.target.files);
          e.target.value = "";
        }}
        required={required}
        disabled={disabled}
        name={name}
        error={isInputHasErr}
      />
      <div className={`position-relative ${wrapperClasses}`}>
        <label
          htmlFor={disabled || isUploading ? undefined : inputId}
          className={`mt-2 upload-field__label ${
            disabled ? "upload-field__label--disabled" : ""
          } ${
            showMultiImageCustomArea
              ? "w-100 d-flex flex-column align-items-stretch gap-2"
              : isFullWidthImage
                ? "d-flex justify-content-center mega-uploaded-image-container position-relative grow-child-panner"
                : ""
          }`}
        >
          {showUploadSuccess ? (
            <UIUploadedFile
              fileName={selectedFileName || pdfFileName || normalizedFieldLabel}
              successLabel={shared.uploadedSuccessfully}
            />
          ) : showSelectedFilePreview ? (
            showUploadProgress && isUploading ? (
              <UIUploadProgress
                fileName={selectedFileName || pdfFileName || normalizedFieldLabel}
                preview={pdfOrImageSingle || img}
                isPdf={selectedIsPdf}
                progress={uploadProgress}
                label={uploadProgressLabel}
                onRemove={disabled ? undefined : clearSelectedFile}
              />
            ) : (
              selectedFilePreview
            )
          ) : showMultiImageCustomArea ? (
            <>
              <div
                className={`upload-custom-area w-100 ${
                  disabled ? "upload-custom-area--disabled" : ""
                } ${isInputHasErr ? "error-border" : ""}`}
                onDragEnter={preventDragDefaults}
                onDragOver={preventDragDefaults}
                onDrop={(e) => {
                  preventDragDefaults(e);
                  if (!disabled) {
                    handleFilesSelected(e.dataTransfer.files);
                  }
                }}
              >
                {uploadArea}
              </div>
              {previewList.length > 0 && (
                <div className="upload-multi-previews">
                  {previewList.map((url, idx) => (
                    <div
                      key={`${url}-${idx}`}
                      className="upload-multi-previews__cell"
                    >
                      <div
                        className="upload-multi-previews__thumb"
                        style={{ backgroundImage: `url(${url})` }}
                        role="presentation"
                      />
                      {!isUploading && onRemoveAt ? (
                        <HighlightOffIcon
                          className="upload-multi-previews__remove"
                          onClick={(event) => {
                            event.preventDefault();
                            event.stopPropagation();
                            onRemoveAt(idx);
                          }}
                        />
                      ) : null}
                    </div>
                  ))}
                </div>
              )}
            </>
          ) : showPdfOrImageAsImage ? (
            <div
              className={`${
                isFullWidthImage
                  ? "position-relative  uploaded-image-container"
                  : ""
              }`}
            >
              <div
                style={{ backgroundImage: `url(${pdfOrImageSingle})` }}
                className={`logoInput ${disabled ? "" : "pointer"} ${
                  isFullWidthImage ? fullWidthImageClassName : ""
                }`}
              ></div>
              {isBluredBackground && (
                <div
                  style={{ backgroundImage: `url(${pdfOrImageSingle})` }}
                  className="blur-image"
                ></div>
              )}
            </div>
          ) : showPdfOrImageAsPdf ? (
            <div
              className={`py-5 px-4 pdf-upload-container ${
                disabled ? "" : "pointer"
              }`}
            >
              <span className="d-flex justify-content-center mb-3">
                <PdfIcon />
              </span>
              <span className="w-100">{pdfFileName.split(".pdf")[0]}</span>
            </div>
          ) : img ? (
            <div
              className={`${
                isFullWidthImage
                  ? "position-relative  uploaded-image-container"
                  : ""
              }`}
            >
              <div
                style={{ backgroundImage: `url(${img})` }}
                className={`logoInput ${disabled ? "" : "pointer"} ${
                  isFullWidthImage ? fullWidthImageClassName : ""
                }`}
              ></div>
              {isBluredBackground && (
                <div
                  style={{ backgroundImage: `url(${img})` }}
                  className="blur-image"
                ></div>
              )}
            </div>
          ) : uploadArea ? (
            <div
              className={`upload-custom-area w-100 ${
                disabled ? "upload-custom-area--disabled" : ""
              } ${isInputHasErr ? "error-border" : ""}`}
              onDragEnter={preventDragDefaults}
              onDragOver={preventDragDefaults}
              onDrop={(e) => {
                preventDragDefaults(e);
                if (!disabled) {
                  handleFilesSelected(e.dataTransfer.files);
                }
              }}
            >
              {uploadArea}
            </div>
          ) : (
            <Button
              variant="contained"
              component="span"
              className={`btn-upload ${isInputHasErr && "error-border"} ${
                isFullWidthImage ? "btn-banner" : ""
              }`}
              disabled={disabled}
            >
              {label}
            </Button>
          )}
        </label>
        {(img || pdf) &&
          !disabled &&
          !isUploading &&
          !showSelectedFilePreview &&
          !showUploadSuccess &&
          !pdfOrImageSingle &&
          !pdf && (
            <div className={`ovelay pointer ${pdf ? "pdf-remove" : ""}`}>
              <HighlightOffIcon
                onClick={(event) => {
                  onChange(null);
                  event.stopPropagation();
                  event.nativeEvent.stopImmediatePropagation();
                }}
              />
            </div>
          )}
      </div>
      {resolvedHelperError ? (
        <FormHelperText
          error
          className={`font-medium fsize-12 ${lang === "en" ? "" : "text-end"}`}
        >
          {resolvedHelperError}
        </FormHelperText>
      ) : null}
    </div>
  );
};

Upload.propTypes = {
  accept: PropTypes.string,
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
  onChange: PropTypes.func.isRequired,
  required: PropTypes.bool,
  disabled: PropTypes.bool,
  img: PropTypes.any,
  name: PropTypes.string,
  multiple: PropTypes.bool,
  className: PropTypes.string,
  onChangeError: PropTypes.func,
  isFullWidthImage: PropTypes.bool,
  fullWidthImageClassName: PropTypes.string,
  id: PropTypes.string,
  isInputHasErr: PropTypes.bool,
  fieldLabel: PropTypes.string,
  isImage: PropTypes.bool,
  isPdf: PropTypes.bool,
  pdf: PropTypes.any,
  isBluredBackground: PropTypes.bool,
  wrapperClasses: PropTypes.string,
  errorMsg: PropTypes.string,
  uploadArea: PropTypes.node,
  onRemoveAt: PropTypes.func,
  isUploading: PropTypes.bool,
  uploadProgress: PropTypes.number,
  uploadProgressLabel: PropTypes.string,
  uploadCompleted: PropTypes.bool,
  showUploadProgress: PropTypes.bool,
  maxFileSizeBytes: PropTypes.number,
  invalidTypeMessage: PropTypes.string,
  invalidSizeMessage: PropTypes.string,
  onPickerDismissedWithoutFile: PropTypes.func,
};

Upload.defaultProps = {
  accept: "image/*",
  label: "Upload",
  required: false,
  disabled: false,
  onRemoveAt: undefined,
  isUploading: false,
  uploadProgress: undefined,
  uploadProgressLabel: undefined,
  uploadCompleted: false,
  showUploadProgress: true,
  maxFileSizeBytes: undefined,
  invalidTypeMessage: undefined,
  invalidSizeMessage: undefined,
};

export default Upload;
